import React, { useRef, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import {
  Paper,
  InputBase,
  Divider,
  Checkbox,
  List,
  CircularProgress,
  ListItemSecondaryAction,
  DialogActions,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Dialog,
  Button,
  Box,
  TextField
} from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Close as CloseIcon } from '@mui/icons-material';

import { observer } from 'mobx-react-lite';
import agent from '../../lib/agent';
import useStores from '../../stores/useStores';

function ProjectPicker() {
  const formEl = useRef(null);
  const projectNameEl = useRef(null);
  const projectDescriptionEl = useRef(null);

  const {
    projectStore,
    editorStore,
    userStore,
    buildingStore,
    uiStore
  } = useStores();

  const { features } = editorStore;
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const { buildings, context } = location.state;
  const [activeStep, setActiveStep] = useState(
    context === 'new-project' ? 1 : 0
  );
  uiStore.setIsSpeedDialOpen(false);

  const projects = [...projectStore.resultMap.values()];
  const [results, setResults] = useState(projects);
  const [checked, setChecked] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [open, setOpen] = useState(buildings);

  const handleStepChange = step => {
    setActiveStep(step);
  };

  const handleKeyUp = event => {
    const { value } = event.target;
    const results = projects.filter(project => {
      return new RegExp(value, 'ig').test(project.name);
    });

    setResults(results);
  };

  const toggleChecked = async value => {
    let newChecked = [...checked];
    if (checked.includes(value.id)) {
      newChecked = newChecked.filter(val => val !== value.id);
      setInProgress([...inProgress, value.id]);
      await agent.Project.removeBuilding(value.id, buildings);
      userStore.addNotification({
        title: `Removed from ${value.name}`,
        type: 'building'
      });

      setInProgress([...inProgress].filter(val => val !== value.id));
    } else {
      userStore.addNotification({
        title: `Added to ${value.name}`,
        type: 'building'
      });
      newChecked.push(value.id);
      agent.Project.addBuilding(value.id, buildings);
    }

    setChecked(newChecked);
  };

  const onCreateNewProject = () => {
    setActiveStep(1);
    editorStore.setFeatures([]);
  };

  const handleClose = () => {
    setActiveStep(0);
    history.push(location.pathname, {
      state: {
        modal: false,
        buildingId: null
      }
    });
    setOpen(false);
  };

  const onSubmit = async () => {
    if (window.location.pathname === '/project/upload') {
      const pickedBuildings = [...buildingStore.pickedResults].map(
        buldingId => ({
          id: buldingId
        })
      );
      const params = {
        buildings: features.filter(feature => {
          return pickedBuildings.some(building => building.id === feature.id);
        }),
        name: projectNameEl.current.value,
        description: projectDescriptionEl.current.value || ''
      };
      handleClose();
      const project = await projectStore.createProject(params);
      if (!project.error) {
        buildingStore.clearPicked();
        editorStore.reset();
        uiStore.setSearchPanelState('search');
        projectStore.fetchAll();
        userStore.addNotification({
          title: `New project: ${project.name}`,
          type: 'project'
        });
        history.push(`/project/${project.id}`);
      }
      return;
    }
    const params = {
      buildings,
      name: projectNameEl.current.value,
      description: projectDescriptionEl.current.value
    };
    handleClose();
    const project = await projectStore.createProject(params);
    if (!project.error) {
      buildingStore.clearPicked();
      editorStore.reset();
      uiStore.setSearchPanelState('search');
      projectStore.fetchAll();
      userStore.addNotification({
        title: `New project: ${project.name}`,
        type: 'project'
      });
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <div
        sx={{
          maxWidth: 400,
          minWidth: 320,
          flexGrow: 1
        }}
      >
        <Paper
          square
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 50,
            paddingLeft: 16,
            backgroundColor: theme.palette.background.default
          }}
        >
          {activeStep === 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <Typography>Select Project</Typography>
              <IconButton
                aria-label="Close"
                className=""
                onClick={handleClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </div>
          )}

          {activeStep === 1 && (
            <>
              {context !== 'new-project' && (
                <IconButton
                  style={{ marginLeft: -14 }}
                  onClick={() => {
                    setActiveStep(0);
                  }}
                  color="primary"
                  component="span"
                  size="large"
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              <Typography sx={{ ml: context === 'new-project' ? -10 : 0 }}>
                Project Details
              </Typography>
            </>
          )}
        </Paper>
        <SwipeableViews
          disabled
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents
        >
          <div key="list">
            <InputBase
              onKeyUp={handleKeyUp}
              style={{
                padding: '16px 16px'
              }}
              placeholder="Search projects..."
              inputProps={{ 'aria-label': 'Search projects' }}
            />
            <Divider orientation="horizontal" />

            <Box p={2} pb={0}>
              <Typography variant="body2">
                Adding {buildingStore.pickedResults.size} selected{' '}
                {buildingStore.pickedResults.size === 1
                  ? 'building'
                  : 'buildings'}{' '}
                to project:
              </Typography>
            </Box>

            <div style={{ height: 300, overflowY: 'scroll' }}>
              <List dense>
                {results.map(result => (
                  <ListItem
                    onClick={() => toggleChecked(result)}
                    key={result.id}
                    button
                  >
                    <ListItemIcon>
                      <Checkbox
                        value={result.id}
                        checked={checked.includes(result.id)}
                        disabled={inProgress.includes(result.id)}
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={result.name} />
                    <ListItemSecondaryAction>
                      {inProgress.includes(result.id) && <CircularProgress />}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </div>
          </div>
          <div key="settings">
            <Paper
              ref={formEl}
              component="form"
              square
              elevation={0}
              onSubmit={onSubmit}
              style={{
                padding: '2rem'
              }}
            >
              <TextField
                required
                fullWidth
                inputRef={projectNameEl}
                label="Project name"
                defaultValue="Untitled Project"
                margin="normal"
              />
              <TextField
                multiline
                inputRef={projectDescriptionEl}
                rows={4}
                fullWidth
                label="Project Description"
                margin="normal"
              />
              <Typography variant="body2">
                Creating project with {buildingStore.pickedResults.size}{' '}
                selected buildings
              </Typography>
            </Paper>
          </div>
        </SwipeableViews>
      </div>
      <DialogActions>
        {activeStep === 0 && (
          <Button
            color="primary"
            style={{ marginRight: 'auto' }}
            onClick={onCreateNewProject}
          >
            Create project
          </Button>
        )}
        <Button
          onClick={() => {
            // buildingStore.clearPicked();
            // editorStore.reset();
            // uiStore.setSearchPanelState('search');
            // handleClose();
            setOpen(false);
            history.goBack();
          }}
          color="primary"
        >
          Cancel
        </Button>

        {activeStep === 0 && (
          <Button
            onClick={() => {
              buildingStore.clearPicked();
              editorStore.reset();
              uiStore.setSearchPanelState('search');
              handleClose();
            }}
            color="secondary"
          >
            Done
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            onClick={() => {
              const isValid = formEl.current.reportValidity();
              if (isValid) onSubmit();
            }}
            color="secondary"
          >
            Create
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default observer(ProjectPicker);
